import React, { useState, useMemo } from "react";
import MathContent from "../../../../customComponents/MathContent";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  AutoAwesome,
  CheckCircle,
  Cancel,
  Flip,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useGetQuizResultQuery } from "../../../../../app/quizApiSlice";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(32px) scale(0.97); }
  to   { opacity: 1; transform: translateX(0)    scale(1); }
`;
const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-32px) scale(0.97); }
  to   { opacity: 1; transform: translateX(0)     scale(1); }
`;
const flipOut = keyframes`
  0%   { transform: rotateY(0deg); }
  100% { transform: rotateY(90deg); }
`;
const flipIn = keyframes`
  0%   { transform: rotateY(-90deg); }
  100% { transform: rotateY(0deg); }
`;
const scorePop = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
`;
const confettiFall = keyframes`
  0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(120px) rotate(540deg); opacity: 0; }
`;

// ─── Socratic reflection prompts for wrong answers ────────────────────────────
const REFLECTIONS = [
  "What concept does this question test?",
  "Why might someone choose the wrong answer here?",
  "How would you explain the correct answer to a classmate?",
  "Can you think of a similar example from your studies?",
  "What would you do differently next time?",
];

// ─── Card face: question side ─────────────────────────────────────────────────
const CardFront = ({ entry, index, total, isWrong }) => (
  <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: { xs: 3, sm: 3.5 } }}>
    {/* Badge */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Box sx={{
        px: 1.5, py: 0.4, borderRadius: "8px",
        bgcolor: isWrong ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
        border: `1px solid ${isWrong ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
        display: "flex", alignItems: "center", gap: 0.6,
      }}>
        {isWrong
          ? <Cancel sx={{ fontSize: 14, color: "#f87171" }} />
          : <CheckCircle sx={{ fontSize: 14, color: "#4ade80" }} />}
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: isWrong ? "#f87171" : "#4ade80", textTransform: "uppercase", letterSpacing: 1 }}>
          {isWrong ? "You got this wrong" : "You got this right"}
        </Typography>
      </Box>
      <Typography sx={{ ml: "auto", fontSize: "0.72rem", color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>
        {index + 1} / {total}
      </Typography>
    </Box>

    {/* Question body */}
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center", px: 1 }}>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 2, textTransform: "uppercase", mb: 1.5 }}>
        Question
      </Typography>
      <Typography component="div" sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.1rem" }, color: "#fff", lineHeight: 1.65 }}>
        <MathContent html={entry.question?.body} />
      </Typography>
    </Box>

    {/* Hint */}
    <Box sx={{ textAlign: "center", mt: 2 }}>
      <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
        <Flip sx={{ fontSize: 14 }} /> Tap "Reveal" to see the answer
      </Typography>
    </Box>
  </Box>
);

// ─── Card face: answer side ───────────────────────────────────────────────────
const CardBack = ({ entry, reflection }) => {
  const correctOpt = entry.question?.options?.find((o) => o.is_answer);
  const studentOpt = entry.question?.options?.find((o) => o.id === entry.option_id);
  const isWrong = studentOpt?.id !== correctOpt?.id;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: { xs: 3, sm: 3.5 }, overflowY: "auto" }}>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 2, textTransform: "uppercase", mb: 2 }}>
        Answer
      </Typography>

      {/* Correct answer */}
      <Box sx={{ p: 2, borderRadius: "14px", mb: 2, bgcolor: "rgba(34,197,94,0.12)", border: "1.5px solid rgba(34,197,94,0.4)" }}>
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#4ade80", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>
          ✓ Correct Answer
        </Typography>
        <Typography component="div" sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff", lineHeight: 1.5 }}>
          <MathContent html={correctOpt?.body} />
        </Typography>
      </Box>

      {/* Student's wrong choice */}
      {isWrong && studentOpt && (
        <Box sx={{ p: 2, borderRadius: "14px", mb: 2, bgcolor: "rgba(239,68,68,0.1)", border: "1.5px solid rgba(239,68,68,0.3)" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#f87171", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>
            ✗ Your Answer
          </Typography>
          <Typography component="div" sx={{ fontWeight: 600, fontSize: "0.9rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
            <MathContent html={studentOpt?.body} />
          </Typography>
        </Box>
      )}

      {/* Explanation */}
      {entry.question?.explanation && (
        <Box sx={{ p: 2, borderRadius: "12px", mb: 2, bgcolor: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#a5b4fc", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>
            💡 Explanation
          </Typography>
          <Typography component="div" sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}>
            <MathContent html={entry.question.explanation} />
          </Typography>
        </Box>
      )}

      {/* Socratic reflection */}
      {isWrong && (
        <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#fcd34d", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>
            🤔 Think About It
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.55, fontStyle: "italic" }}>
            {reflection}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Single flashcard with flip animation ─────────────────────────────────────
const FlashCard = ({ entry, index, total, direction, onPrev, onNext, isFirst, isLast }) => {
  const [revealed, setRevealed] = useState(false);
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState(null);

  const isWrong = !entry.question?.options?.find(
    (o) => o.id === entry.option_id && o.is_answer
  );
  const reflection = REFLECTIONS[index % REFLECTIONS.length];

  const handleReveal = () => {
    if (flipping) return;
    setFlipping(true);
    setFlipDir("out");
    setTimeout(() => {
      setRevealed(true);
      setFlipDir("in");
      setTimeout(() => { setFlipping(false); setFlipDir(null); }, 300);
    }, 280);
  };

  const handleNavigate = (fn) => { setRevealed(false); setFlipDir(null); fn(); };

  const slideAnim = direction === "forward"
    ? `${slideIn} 0.3s ease`
    : `${slideInLeft} 0.3s ease`;

  const flipAnim = flipDir === "out"
    ? `${flipOut} 0.28s ease forwards`
    : flipDir === "in"
    ? `${flipIn} 0.28s ease forwards`
    : undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Card */}
      <Box sx={{
        minHeight: { xs: 340, sm: 380 },
        borderRadius: "24px",
        background: isWrong
          ? "linear-gradient(145deg, #1a0a0a 0%, #2d1010 50%, #1f0e0e 100%)"
          : "linear-gradient(145deg, #081a0a 0%, #0d2d10 50%, #081a0a 100%)",
        border: `1.5px solid ${isWrong ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`,
        boxShadow: `0 12px 40px ${isWrong ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.1)"}`,
        animation: flipAnim || slideAnim,
        overflow: "hidden",
        position: "relative",
      }}>
        <Box sx={{ position: "absolute", top: -40, right: -40, width: 140, height: 140, borderRadius: "50%", bgcolor: isWrong ? "rgba(239,68,68,0.06)" : "rgba(34,197,94,0.06)", pointerEvents: "none" }} />
        {!revealed
          ? <CardFront entry={entry} index={index} total={total} isWrong={isWrong} />
          : <CardBack entry={entry} reflection={reflection} />}
      </Box>

      {/* Navigation */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          onClick={() => handleNavigate(onPrev)}
          disabled={isFirst}
          sx={{ width: 44, height: 44, bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: isFirst ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>

        {!revealed ? (
          <Button
            fullWidth
            onClick={handleReveal}
            disabled={flipping}
            startIcon={<Flip />}
            sx={{ background: "linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)", color: "#fff", fontWeight: 800, borderRadius: "14px", py: 1.3, boxShadow: "0 4px 16px rgba(79,70,229,0.4)", "&:hover": { background: "linear-gradient(90deg, #4338ca 0%, #6d28d9 100%)" } }}
          >
            Reveal Answer
          </Button>
        ) : (
          <Button
            fullWidth
            onClick={() => handleNavigate(onNext)}
            disabled={isLast}
            endIcon={<ArrowForward />}
            sx={{
              background: isLast ? "rgba(255,255,255,0.07)" : "linear-gradient(90deg, #1565C0 0%, #42A5F5 100%)",
              color: isLast ? "rgba(255,255,255,0.3)" : "#fff",
              fontWeight: 800, borderRadius: "14px", py: 1.3,
              boxShadow: isLast ? "none" : "0 4px 16px rgba(66,165,245,0.35)",
              "&.Mui-disabled": { background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.3)" },
            }}
          >
            {isLast ? "Last card" : "Next →"}
          </Button>
        )}

        <IconButton
          onClick={() => handleNavigate(onNext)}
          disabled={isLast}
          sx={{ width: 44, height: 44, bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: isLast ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}
        >
          <ArrowForward fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Done screen ──────────────────────────────────────────────────────────────
const DoneScreen = ({ wrongCount, totalCount, onDone }) => {
  const dots = Array.from({ length: 14 }, (_, i) => ({
    left: `${((i * 7 + 5) % 90) + 5}%`,
    color: ["#FF6B9D","#FFD700","#4FC3F7","#A5D6A7","#FF8A65","#CE93D8"][i % 6],
    delay: `${(i * 0.08).toFixed(2)}s`,
    size: 5 + (i % 4) * 2,
  }));
  return (
    <Box sx={{ textAlign: "center", position: "relative", overflow: "hidden", py: 4 }}>
      {dots.map((d, i) => (
        <Box key={i} sx={{ position: "absolute", top: "-10px", left: d.left, width: d.size, height: d.size, borderRadius: "50%", bgcolor: d.color, animation: `${confettiFall} 1.2s ${d.delay} ease-out forwards` }} />
      ))}
      <Typography sx={{ fontSize: "3.5rem", mb: 1 }}>🎉</Typography>
      <Typography sx={{ fontWeight: 900, fontSize: "1.4rem", color: "#fff", mb: 0.5, animation: `${scorePop} 0.5s ease both` }}>
        Review Complete!
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.55)", mb: 3 }}>
        You reviewed {wrongCount} wrong answer{wrongCount !== 1 ? "s" : ""} out of {totalCount} questions
      </Typography>
      <Button
        onClick={onDone}
        variant="contained"
        sx={{ background: "linear-gradient(135deg, #059669, #34d399)", color: "#fff", fontWeight: 800, borderRadius: "14px", px: 4, py: 1.4, boxShadow: "0 6px 20px rgba(5,150,105,0.4)", "&:hover": { background: "linear-gradient(135deg, #047857, #10b981)" } }}
      >
        Done
      </Button>
    </Box>
  );
};

// ─── ReviewStep ───────────────────────────────────────────────────────────────
// Props: quizId, onBack (optional), onDone (optional)
const ReviewStep = ({ quizId, onBack, onDone }) => {
  const { data, isLoading } = useGetQuizResultQuery({ quiz_id: quizId });
  const [cardIndex, setCardIndex] = useState(0);
  const [direction, setDirection] = useState("forward");
  const [done, setDone] = useState(false);

  // Wrong answers first for focus, then correct for reinforcement
  const deck = useMemo(() => {
    if (!data?.answers) return [];
    const isCorrect = (a) => {
      const c = a.question?.options?.find((o) => o.is_answer);
      return c && a.option_id === c.id;
    };
    const wrong = data.answers.filter((a) => !isCorrect(a));
    const right = data.answers.filter((a) => isCorrect(a));
    return [...wrong, ...right];
  }, [data]);

  const wrongCount = useMemo(
    () => deck.filter((e) => {
      const c = e.question?.options?.find((o) => o.is_answer);
      return c && e.option_id !== c.id;
    }).length,
    [deck]
  );

  const totalCards = deck.length;
  const progress = totalCards > 0 ? ((cardIndex + 1) / totalCards) * 100 : 0;

  const handleNext = () => {
    if (cardIndex < totalCards - 1) { setDirection("forward"); setCardIndex((i) => i + 1); }
    else { setDone(true); }
  };
  const handlePrev = () => {
    if (cardIndex > 0) { setDirection("back"); setCardIndex((i) => i - 1); }
  };

  if (isLoading) {
    return (
      <Box sx={{ background: "linear-gradient(160deg, #0a0f1e 0%, #111827 100%)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#6366f1" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ background: "linear-gradient(160deg, #0a0f1e 0%, #111827 50%, #0a0f1e 100%)", minHeight: "100vh", px: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 9 }, pb: 4 }}>

      {/* ── Header ── */}
      <Box sx={{ pt: 2.5, pb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small" sx={{ color: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Box sx={{ width: 36, height: 36, borderRadius: "12px", background: "linear-gradient(135deg, #4f46e5, #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 14px rgba(79,70,229,0.4)" }}>
          <AutoAwesome sx={{ fontSize: 18, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "0.58rem", color: "rgba(255,255,255,0.4)", letterSpacing: 3, textTransform: "uppercase" }}>Review</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff", lineHeight: 1.2 }}>Answer Review</Typography>
        </Box>
        {!done && (
          <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "rgba(255,255,255,0.4)" }}>
            {cardIndex + 1} / {totalCards}
          </Typography>
        )}
      </Box>

      {/* ── Progress bar ── */}
      {!done && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 4, borderRadius: "4px", mb: 2.5, bgcolor: "rgba(255,255,255,0.07)", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)", borderRadius: "4px" } }}
        />
      )}

      {/* ── Content ── */}
      {done ? (
        <DoneScreen wrongCount={wrongCount} totalCount={data?.answers?.length || 0} onDone={onDone} />
      ) : totalCards === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ fontSize: "2.5rem", mb: 1 }}>🎯</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff", mb: 0.5 }}>No answers to review</Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)", mb: 3 }}>No recorded answers found for this quiz.</Typography>
          {onDone && <Button onClick={onDone} variant="outlined" sx={{ color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.3)", fontWeight: 700, borderRadius: "12px" }}>Go Back</Button>}
        </Box>
      ) : (
        <>
          {/* Wrong / correct count chips */}
          <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
            <Box sx={{ px: 1.5, py: 0.8, borderRadius: "10px", bgcolor: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", display: "flex", alignItems: "center", gap: 0.6 }}>
              <Cancel sx={{ fontSize: 14, color: "#f87171" }} />
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#f87171" }}>{wrongCount} wrong</Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.8, borderRadius: "10px", bgcolor: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.3)", display: "flex", alignItems: "center", gap: 0.6 }}>
              <CheckCircle sx={{ fontSize: 14, color: "#4ade80" }} />
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#4ade80" }}>{totalCards - wrongCount} correct</Typography>
            </Box>
            {cardIndex < wrongCount && (
              <Box sx={{ px: 1.5, py: 0.8, borderRadius: "10px", bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(255,255,255,0.45)" }}>Focusing on wrong answers first</Typography>
              </Box>
            )}
          </Box>

          <FlashCard
            key={cardIndex}
            entry={deck[cardIndex]}
            index={cardIndex}
            total={totalCards}
            direction={direction}
            onPrev={handlePrev}
            onNext={handleNext}
            isFirst={cardIndex === 0}
            isLast={cardIndex === totalCards - 1}
          />

          <Box sx={{ textAlign: "center", mt: 2.5 }}>
            <Button
              onClick={() => setDone(true)}
              sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 600, fontSize: "0.78rem", "&:hover": { color: "rgba(255,255,255,0.6)", bgcolor: "rgba(255,255,255,0.05)" }, borderRadius: "10px" }}
            >
              Skip to end
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ReviewStep;
